import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../../../firebase';
import { collection, onSnapshot, query, orderBy, doc, addDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { ref as sRef, getDownloadURL, deleteObject } from 'firebase/storage';
import { uploadOptimizedImage } from '../../../services/imageOptimizer';
import { Star, Plus, Edit2, Trash2, Search, User, ImageIcon, Loader2, X, MessageSquareText, Check, Youtube, Calendar } from 'lucide-react';
import { ReviewModel, GRADE_TYPES, SUBJECT_TYPES } from '../../../models';

const ReviewsRoot: React.FC = () => {
    const [reviews, setReviews] = useState<ReviewModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<'all' | 'student' | 'parent'>('all');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingReview, setEditingReview] = useState<ReviewModel | null>(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form fields
    const [studentName, setStudentName] = useState('');
    const [reviewerRole, setReviewerRole] = useState<'student' | 'parent'>('student');
    const [school, setSchool] = useState('');
    const [grade, setGrade] = useState('');
    const [subject, setSubject] = useState('');
    const [rating, setRating] = useState(5);
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [content, setContent] = useState('');
    const [isBest, setIsBest] = useState(false);
    const [tagsInput, setTagsInput] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

    useEffect(() => {
        const q = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: ReviewModel[] = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() } as ReviewModel);
            });
            setReviews(list);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching reviews:', error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);
    // Set form fields for editing
    const handleEdit = (review: ReviewModel) => {
        setEditingReview(review);
        setStudentName(review.studentName);
        setReviewerRole(review.reviewerRole);
        setSchool(review.school || '');
        setGrade(review.grade || '');
        setSubject(review.subject || '');
        setRating(review.rating);
        setYoutubeUrl(review.youtubeUrl || '');
        setContent(review.content);
        setIsBest(review.isBest || false);
        setTagsInput(review.tags ? review.tags.join(', ') : '');
        setImageUrl(review.imageUrl || '');
        setImagePreview(review.imageUrl || null);
        setSelectedImageFile(null);
        setIsFormOpen(true);
    };

    const handleAddNew = () => {
        setEditingReview(null);
        setStudentName('');
        setReviewerRole('student');
        setSchool('');
        setGrade('');
        setSubject('');
        setRating(5);
        setYoutubeUrl('');
        setContent('');
        setIsBest(false);
        setTagsInput('');
        setImageUrl('');
        setImagePreview(null);
        setSelectedImageFile(null);
        setIsFormOpen(true);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 업로드할 수 있습니다.');
            return;
        }

        setSelectedImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleUploadImage = async (file: File): Promise<string> => {
        const fileName = `${Date.now()}_${file.name.replace(/\.[^/.]+$/, "")}.webp`;
        const storageRef = sRef(storage, `reviews/${fileName}`);
        const snapshot = await uploadOptimizedImage(storageRef, file, { maxWidth: 1200, quality: 0.85 });
        return await getDownloadURL(snapshot.ref);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!studentName.trim()) {
            alert('작성자 이름을 입력해주세요.');
            return;
        }
        if (!content.trim()) {
            alert('내용을 입력해주세요.');
            return;
        }

        setUploadingImage(true);
        try {
            let finalImageUrl = imageUrl;

            // Upload new image if selected
            if (selectedImageFile) {
                finalImageUrl = await handleUploadImage(selectedImageFile);
            }

            const parsedTags = tagsInput
                ? tagsInput.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
                : [];

            const docData = {
                studentName: studentName.trim(),
                reviewerRole,
                school: school.trim(),
                grade: grade.trim(),
                subject: subject.trim(),
                rating,
                youtubeUrl: youtubeUrl.trim(),
                content: content.trim(),
                isBest,
                tags: parsedTags,
                imageUrl: finalImageUrl,
                createdAt: editingReview ? editingReview.createdAt : Date.now()
            };
            if (editingReview) {
                // Update
                await setDoc(doc(db, 'reviews', editingReview.id), docData, { merge: true });
                alert('수강후기가 성공적으로 수정되었습니다.');
            } else {
                // Create new
                await addDoc(collection(db, 'reviews'), docData);
                alert('수강후기가 성공적으로 등록되었습니다.');
            }

            setIsFormOpen(false);
            setEditingReview(null);
            setSelectedImageFile(null);
            setImagePreview(null);
        } catch (error) {
            console.error('Error saving review:', error);
            alert('수강후기 저장 중 오류가 발생했습니다.');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleDelete = async (review: ReviewModel) => {
        if (!confirm(`'${review.studentName}'님의 수강후기를 삭제하시겠습니까?`)) return;

        try {
            // Delete Firestore doc
            await deleteDoc(doc(db, 'reviews', review.id));

            // Delete storage file if exists
            if (review.imageUrl && review.imageUrl.includes('firebase')) {
                try {
                    const storageRef = sRef(storage, review.imageUrl);
                    await deleteObject(storageRef);
                } catch (storageErr) {
                    console.warn('Storage file deletion failed or skipped:', storageErr);
                }
            }
            alert('수강후기가 삭제되었습니다.');
        } catch (error) {
            console.error('Error deleting review:', error);
            alert('삭제 중 오류가 발생했습니다.');
        }
    };

    // Filter reviews
    const filteredReviews = reviews.filter(r => {
        const matchesRole = roleFilter === 'all' || r.reviewerRole === roleFilter;
        const matchesSearch =
            r.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.youtubeUrl || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.school || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.subject || '').toLowerCase().includes(searchTerm.toLowerCase());
        return matchesRole && matchesSearch;
    });

    const renderStars = (count: number) => {
        return (
            <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map(star => (
                    <Star
                        key={star}
                        size={14}
                        className={star <= count ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden select-none">
            {/* Header */}
            <div className="bg-white border-b border-slate-100 px-8 py-6 flex items-center justify-between shrink-0">
                <div className="space-y-1">
                    <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
                        <Star className="text-indigo-600 fill-indigo-600" size={24} />
                        수강후기 관리
                    </h1>
                    <p className="text-slate-500 font-medium text-xs md:text-sm">
                        랜딩 페이지에 노출되는 생생한 수강생 및 학부모 수강후기 콘텐츠를 등록하고 관리합니다.
                    </p>
                </div>

                {!isFormOpen && (
                    <button
                        onClick={handleAddNew}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-5 py-3 font-black text-sm flex items-center gap-2 transition-all shadow-lg shadow-indigo-100 active:scale-95 cursor-pointer"
                    >
                        <Plus size={16} />
                        새 수강후기 등록
                    </button>
                )}
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto p-8">
                {isFormOpen ? (
                    // FORM VIEW
                    <div className="bg-white rounded-[2rem] border border-slate-100 p-6 md:p-8 shadow-sm max-w-3xl mx-auto animate-in fade-in duration-300">
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                            <h2 className="text-lg font-black text-slate-800">
                                {editingReview ? '수강후기 수정' : '새 수강후기 등록'}
                            </h2>
                            <button
                                onClick={() => setIsFormOpen(false)}
                                className="text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            {/* Profile Image & Role/Name in same row */}
                            <div className="flex flex-col md:flex-row gap-6">
                                {/* Profile Image Upload */}
                                <div className="flex flex-col items-center justify-center shrink-0">
                                    <span className="text-xs font-black text-slate-550 mb-2">프로필 이미지</span>
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className="w-24 h-24 rounded-full border-2 border-dashed border-slate-200 bg-slate-50 hover:bg-slate-100/50 flex flex-col items-center justify-center overflow-hidden cursor-pointer relative group transition-all"
                                    >
                                        {imagePreview ? (
                                            <>
                                                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <ImageIcon className="text-white" size={18} />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <User className="text-slate-350" size={28} />
                                                <span className="text-[10px] text-slate-400 font-bold mt-1">사진 선택</span>
                                            </>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        className="hidden"
                                        accept="image/*"
                                    />
                                    {imagePreview && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setImagePreview(null);
                                                setSelectedImageFile(null);
                                                setImageUrl('');
                                            }}
                                            className="text-[10px] text-red-500 hover:underline font-bold mt-1"
                                        >
                                            사진 지우기
                                        </button>
                                    )}
                                </div>

                                {/* Inputs */}
                                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-1.5 col-span-1">
                                        <label className="text-xs font-black text-slate-500 pl-1">작성자 역할</label>
                                        <div className="flex p-1 bg-slate-100 rounded-xl">
                                            <button
                                                type="button"
                                                onClick={() => setReviewerRole('student')}
                                                className={`flex-1 py-2 rounded-lg text-xs font-black transition-all ${reviewerRole === 'student' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}
                                            >
                                                수강생 (학생)
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setReviewerRole('parent')}
                                                className={`flex-1 py-2 rounded-lg text-xs font-black transition-all ${reviewerRole === 'parent' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}
                                            >
                                                학부모
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-1.5 col-span-1">
                                        <label className="text-xs font-black text-slate-500 pl-1">작성자 이름</label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                            placeholder="예: 김철수"
                                            value={studentName}
                                            onChange={(e) => setStudentName(e.target.value)}
                                        />
                                    </div>

                                    <div className="space-y-1.5 col-span-1">
                                        <label className="text-xs font-black text-slate-500 pl-1">학교 (선택)</label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                            placeholder="예: 정성우고등학교"
                                            value={school}
                                            onChange={(e) => setSchool(e.target.value)}
                                        />
                                    </div>

                                    <div className="space-y-1.5 col-span-1">
                                        <label className="text-xs font-black text-slate-500 pl-1">학년 (선택)</label>
                                        <select
                                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                            value={grade}
                                            onChange={(e) => setGrade(e.target.value)}
                                        >
                                            <option value="">선택 안 함</option>
                                            {GRADE_TYPES.map((g) => (
                                                <option key={g} value={g}>{g}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Second Row: Subject, Rating, Best Review */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-black text-slate-500 pl-1">수강 과목 (선택)</label>
                                    <select
                                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                    >
                                        <option value="">선택 안 함</option>
                                        {SUBJECT_TYPES.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-black text-slate-500 pl-1">별점</label>
                                    <select
                                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                        value={rating}
                                        onChange={(e) => setRating(Number(e.target.value))}
                                    >
                                        <option value={5}>⭐⭐⭐⭐⭐ (5점)</option>
                                        <option value={4}>⭐⭐⭐⭐ (4점)</option>
                                        <option value={3}>⭐⭐⭐ (3점)</option>
                                        <option value={2}>⭐⭐ (2점)</option>
                                        <option value={1}>⭐ (1점)</option>
                                    </select>
                                </div>

                                <div className="flex items-center justify-start pl-2 pt-6">
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={isBest}
                                            onChange={(e) => setIsBest(e.target.checked)}
                                            className="w-4.5 h-4.5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
                                        />
                                        <span className="text-sm font-bold text-slate-700">우수 후기 (BEST 지정)</span>
                                    </label>
                                </div>
                            </div>

                            {/* Tags Input */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-black text-slate-500 pl-1">
                                    태그 (선택 - 쉼표로 구분)
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                    placeholder="예: 내신 1등급, 성적향상, 친절한 개별케어"
                                    value={tagsInput}
                                    onChange={(e) => setTagsInput(e.target.value)}
                                />
                            </div>

                            {/* Youtube Link */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-black text-slate-500 pl-1">유튜브 영상 링크 (선택)</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                    placeholder="예: https://www.youtube.com/watch?v=... 또는 https://youtu.be/..."
                                    value={youtubeUrl}
                                    onChange={(e) => setYoutubeUrl(e.target.value)}
                                />
                            </div>

                            {/* Content */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-black text-slate-500 pl-1">후기 상세 내용</label>
                                <textarea
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800 h-48 resize-none"
                                    placeholder="수강하면서 느꼈던 점과 학원의 장점을 상세히 입력해 주세요."
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsFormOpen(false)}
                                    className="px-6 py-3 border border-slate-200 text-slate-500 rounded-xl font-black text-sm hover:bg-slate-50 transition-all active:scale-95 cursor-pointer"
                                >
                                    취소
                                </button>
                                <button
                                    type="submit"
                                    disabled={uploadingImage}
                                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-100 disabled:opacity-50 active:scale-95 cursor-pointer"
                                >
                                    {uploadingImage ? (
                                        <>
                                            <Loader2 className="animate-spin" size={16} />
                                            저장 중...
                                        </>
                                    ) : (
                                        <>
                                            <Check size={16} />
                                            {editingReview ? '수정 완료' : '후기 등록'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                ) : (
                    // LIST VIEW
                    <div className="flex flex-col h-full">
                        {/* Table controls */}
                        <div className="bg-white border border-slate-100 rounded-2xl p-4 md:p-6 mb-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 shadow-sm">
                            {/* Search */}
                            <div className="relative flex-1 max-w-md">
                                <input
                                    type="text"
                                    placeholder="이름, 학교, 제목, 내용 검색"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 pl-10 text-slate-700 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-sm font-medium transition-all"
                                />
                                <Search className="absolute left-3.5 top-3.5 text-slate-400" size={16} />
                            </div>

                            {/* Filter */}
                            <div className="flex p-0.5 bg-slate-100 rounded-xl">
                                {(['all', 'student', 'parent'] as const).map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setRoleFilter(tab)}
                                        className={`px-4 py-2 rounded-lg text-xs font-black transition-all cursor-pointer ${roleFilter === tab ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                    >
                                        {tab === 'all' ? '전체' : tab === 'student' ? '수강생' : '학부모'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* List Grid of Cards */}
                        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                            {loading ? (
                                <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm flex flex-col items-center justify-center py-20 text-slate-400">
                                    <Loader2 className="animate-spin text-indigo-600 mb-2" size={32} />
                                    <p className="text-sm font-medium">수강후기 목록을 불러오는 중...</p>
                                </div>
                            ) : filteredReviews.length === 0 ? (
                                <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm text-center py-24 text-slate-400">
                                    <MessageSquareText className="mx-auto text-slate-300 mb-3" size={48} />
                                    <p className="font-bold text-slate-700">등록된 수강후기가 없습니다.</p>
                                    <p className="text-xs text-slate-400 mt-1">상단의 등록 단추를 통해 첫 수강후기를 올려보세요.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pb-8">
                                    {filteredReviews.map((review) => (
                                        <div
                                            key={review.id}
                                            className={`bg-white rounded-3xl border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 flex flex-col justify-between overflow-hidden relative group ${
                                                review.isBest
                                                    ? 'border-amber-200 ring-2 ring-amber-500/5'
                                                    : 'border-slate-100 shadow-sm'
                                            }`}
                                        >
                                            {/* Best Badge Background Highlight */}
                                            {review.isBest && (
                                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-300 to-amber-500"></div>
                                            )}

                                            <div className="p-6 pb-4 space-y-4">
                                                {/* Header: Avatar, Name, Stars */}
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-3">
                                                        {review.imageUrl ? (
                                                            <img
                                                                src={review.imageUrl}
                                                                alt=""
                                                                className="w-10 h-10 rounded-full object-cover border border-slate-100"
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100">
                                                                <User size={16} />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-black text-sm text-slate-800">{review.studentName}</span>
                                                                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${
                                                                    review.reviewerRole === 'student'
                                                                        ? 'bg-indigo-50 text-indigo-600'
                                                                        : 'bg-emerald-50 text-emerald-600'
                                                                }`}>
                                                                    {review.reviewerRole === 'student' ? '수강생' : '학부모'}
                                                                </span>
                                                            </div>
                                                            <p className="text-[11px] text-slate-400 font-bold mt-0.5">
                                                                {review.school || '-'} {review.grade || ''}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col items-end gap-1.5">
                                                        {renderStars(review.rating)}
                                                        {review.isBest && (
                                                            <span className="bg-gradient-to-r from-amber-400 to-amber-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full shadow-sm">
                                                                ★ BEST
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Meta Info: Subject */}
                                                {review.subject && (
                                                    <span className="inline-block bg-indigo-50 text-indigo-750 px-2 py-0.5 rounded text-[10px] font-black">
                                                        {review.subject}
                                                    </span>
                                                )}

                                                {/* Content */}
                                                <div className="space-y-2">
                                                    {review.youtubeUrl && (
                                                        <a
                                                            href={review.youtubeUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1.5 text-[11px] text-red-600 hover:text-red-700 font-black transition-colors bg-red-50 hover:bg-red-100/50 px-2.5 py-1 rounded-full w-fit cursor-pointer"
                                                        >
                                                            <Youtube size={12} className="text-red-500 fill-current" />
                                                            동영상 후기 보기
                                                        </a>
                                                    )}
                                                    <p className="text-xs text-slate-650 leading-relaxed font-medium line-clamp-4 whitespace-pre-wrap break-all">
                                                        {review.content}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Footer Actions */}
                                            <div className="px-6 py-3.5 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between mt-auto">
                                                <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1">
                                                    <Calendar size={12} />
                                                    {new Date(review.createdAt).toLocaleDateString('ko-KR', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </span>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => handleEdit(review)}
                                                        className="p-2 hover:bg-slate-100 text-slate-500 hover:text-slate-800 rounded-xl transition-colors cursor-pointer"
                                                        title="수정"
                                                    >
                                                        <Edit2 size={13} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(review)}
                                                        className="p-2 hover:bg-red-50 text-slate-400 hover:text-red-650 rounded-xl transition-colors cursor-pointer"
                                                        title="삭제"
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewsRoot;
